import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext.js';
import axiosInstance from '../../../api/axiosConfig.js';
import { FiEye, FiTrash2, FiEdit2, FiList, FiChevronDown, FiChevronUp, FiUserPlus, FiMessageSquare } from 'react-icons/fi';
import ReassignTaskModal from '../components/modals/ReassignTaskModal.js';
import ViewTaskModal from '../components/modals/ViewTaskModal.js';
import EditTaskModal from '../components/modals/EditTaskModal.js';
import CreateSubtaskModal from '../components/modals/CreateSubtaskModal.js';
import CommentsModal from '../components/modals/CommentsModal.js';
import ApprovalModal from '../components/modals/ApprovalModal.js';
import VerifyTaskModal from '../components/modals/VerifyTaskModal.js';
import TaskStatistics from '../components/TaskStatistics.js';
import TaskFilters from '../components/TaskFilters.js';
import TaskRow from '../components/TaskRow.js';
import TaskTabs from '../components/TaskTabs.js';
import TaskTable from '../components/TaskTable.js';
import TaskHeader from '../components/TaskHeader.js';
import CreateTaskModal from '../components/modals/CreateTaskModal.js';
import UserSelectSlideOver from '../components/UserSelectSlideOver.js';
import Pagination from '../components/Pagination.js';
import { hasCardAccess, hasCardAction } from '../../../utils/permissionHelper.js';
import { 
    formatDate, 
    formatDateTime, 
    getPriorityColor, 
    getStatusBadge, 
    getApprovalStatusBadge, 
    isOverdue,
    getTabTitle
} from '../utils/taskUtils.js';
import * as taskService from '../services/taskService.js';
import { useTaskManagement } from '../hooks/useTaskManagement.js';
import { useTaskModals } from '../hooks/useTaskModals.js';
import receptionBg from '../../../assets/images/backgrounds/reception-bg.png';
import './TaskManagement.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const TaskManagement = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    
    // Permission-based access control.
    //
    // `canApprove` and task VISIBILITY are deliberately independent:
    //   canApprove  -> may approve/reject tasks assigned to me       (a permission)
    //   scope       -> whose tasks I can see, from Department.heads   (an org role)
    // Visibility arrives from the backend as `scope` (see the hook below); it is
    // never re-derived here, which is what previously let the two disagree.
    const userPermissions = user?.permissions || { cards: [], actions: [] };
    const canApprove = user?.role === 'system_admin' || hasCardAction(userPermissions, 'task-management', 'approve');
    const canCreate = user?.role === 'system_admin' || hasCardAction(userPermissions, 'task-management', 'create');

    // Drives the approval UI only: tab 1 renamed to "Approval Requests",
    // approve/reject buttons, approval-status column and filters.
    const isManager = user?.role === 'system_manager' || (canApprove && user?.role !== 'system_admin');

    // UI State
    const [activeTab, setActiveTab] = useState('assigned-to-me');
    const [viewMode, setViewMode] = useState('list');
    const [groupBy, setGroupBy] = useState('status');
    const [searchTerm, setSearchTerm] = useState('');
    // Debounced copy of searchTerm: API calls fire 400ms after typing stops,
    // instead of on every keystroke
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(['todo', 'in-progress', 'review']); // All except completed
    const [priorityFilter, setPriorityFilter] = useState([]); // Array for multi-select
    const [approvalStatusFilter, setApprovalStatusFilter] = useState('');
    const [overdueFilter, setOverdueFilter] = useState(false);
    const [taskTypeFilter, setTaskTypeFilter] = useState(''); // '' = all, 'regular', 'routine'
    const [currentPage, setCurrentPage] = useState(1);
    const [activeStatCard, setActiveStatCard] = useState(null);
    const [syncingTodo, setSyncingTodo] = useState(false);
    
    // Modal State - Consolidated in custom hook
    const modals = useTaskModals();
    const [approvalAction, setApprovalAction] = useState('');
    const [approvalRemarks, setApprovalRemarks] = useState('');
    const [verifyAction, setVerifyAction] = useState('');
    const [verifyRemarks, setVerifyRemarks] = useState('');
    
    // Form State
    const [formData, setFormData] = useState({
        title: '', description: '', assignedTo: [], priority: 'medium', dueDate: '', dueTime: '', tags: ''
    });
    const [attachmentFiles, setAttachmentFiles] = useState([]);
    const [linkTaskId, setLinkTaskId] = useState('');
    const [linkType, setLinkType] = useState('related');
    const [linkSearchQuery, setLinkSearchQuery] = useState('');
    const [linkSearchResults, setLinkSearchResults] = useState([]);
    const [linkSearchLoading, setLinkSearchLoading] = useState(false);
    const [selectedTaskForLink, setSelectedTaskForLink] = useState(null);
    const [showLinkDropdown, setShowLinkDropdown] = useState(false);

    // Custom Hook - handles all data fetching and business logic
    const {
        tasks,
        setTasks,
        groupedTasks,
        setGroupedTasks,
        users,
        stats,
        managerStats,
        loading,
        totalPages,
        selectedTask,
        setSelectedTask,
        subtasks,
        subtasksLoading,
        expandedGroups,
        scope,
        fetchTaskDetails,
        fetchSubtasks,
        handleCreateTask: createTask,
        handleStatusChange: updateStatus,
        handleDeleteTask: deleteTask,
        handleAddComment,
        handleMarkCommentsAsRead,
        handleReassign: reassignTask,
        handleApprovalAction: approvalAction_,
        handleVerifyAction: verifyAction_,
        handleUpdateTask: updateTask,
        handleCreateSubtask: createSubtask,
        handleSubtaskStatusChange: updateSubtaskStatus,
        handleAddConnection,
        handleRemoveConnection,
        toggleGroup,
        refreshTasks,
        fetchStats
    } = useTaskManagement({
        activeTab,
        currentPage,
        searchTerm: debouncedSearchTerm,
        statusFilter,
        priorityFilter,
        approvalStatusFilter,
        overdueFilter,
        taskTypeFilter,
        viewMode,
        groupBy,
        isManager,
        user
    });

    // Third tab: "Department Tasks" for a department head, "All Tasks" for an
    // admin, hidden entirely for everyone else.
    const canSeeBeyondOwn = scope === 'department' || scope === 'all';

    // If the third tab goes away underneath us (heads reassigned, role changed),
    // don't strand the user on a tab the API will now refuse.
    useEffect(() => {
        if (activeTab === 'all-tasks' && !canSeeBeyondOwn) {
            setActiveTab('assigned-to-me');
        }
    }, [activeTab, canSeeBeyondOwn]);

    // Debounce the search term so we don't hit the API on every keystroke
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // When Enhanced View Modal is open and task is a parent, load subtasks
    useEffect(() => {
        if (modals.showEnhancedViewModal && selectedTask && selectedTask._id && !selectedTask.parentTask) {
            fetchSubtasks(selectedTask._id);
        }
    }, [modals.showEnhancedViewModal, selectedTask?._id, fetchSubtasks]);

    // Reset link-picker state when selected task changes or modal closes
    useEffect(() => {
        if (!modals.showEnhancedViewModal || !selectedTask) {
            setLinkSearchQuery('');
            setLinkSearchResults([]);
            setSelectedTaskForLink(null);
            setShowLinkDropdown(false);
        }
    }, [modals.showEnhancedViewModal, selectedTask?._id]);

    // Debounced search for "Add link" task picker
    useEffect(() => {
        if (!selectedTask?._id) return;
        const q = linkSearchQuery.trim();
        if (!q) {
            setLinkSearchResults([]);
            setShowLinkDropdown(false);
            return;
        }
        const t = setTimeout(async () => {
            setLinkSearchLoading(true);
            try {
                const res = await taskService.searchTasksForLinking(q, selectedTask._id, 20);
                setLinkSearchResults(res?.data || []);
                setShowLinkDropdown(true);
            } catch (err) {
                setLinkSearchResults([]);
            } finally {
                setLinkSearchLoading(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [linkSearchQuery, selectedTask?._id]);

    // Auto-adjust groupBy when switching tabs
    useEffect(() => {
        if (viewMode === 'grouped') {
            if (isManager && activeTab === 'assigned-to-me' && groupBy !== 'approvalStatus') {
                setGroupBy('approvalStatus');
            } else if ((!isManager || activeTab !== 'assigned-to-me') && groupBy === 'approvalStatus') {
                setGroupBy('status');
            }
        }
    }, [activeTab, isManager, viewMode, groupBy]);

    // Wrapper handlers that use the custom hook
    const handleCreateTaskSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.assignedTo || formData.assignedTo.length === 0) {
            alert('Please select at least one user to assign the task to');
            return;
        }
        
        const timeToUse = formData.dueTime || '23:59';
        const dueDateTimeString = `${formData.dueDate}T${timeToUse}:00`;
        
        const taskData = {
            title: formData.title,
            description: formData.description,
            assignedTo: formData.assignedTo,
            priority: formData.priority,
            dueDate: dueDateTimeString,
            tags: formData.tags
        };

        const result = await createTask(taskData, attachmentFiles);
        if (result.success) {
            modals.setShowCreateModal(false);
            resetForm();
            alert('Task created successfully');
        } else {
            alert(result.message);
        }
    };

    const handleSyncTodo = async () => {
        setSyncingTodo(true);
        try {
            const result = await taskService.syncFromTodo();
            if (result.success) {
                refreshTasks();
                fetchStats();
                alert(result.message);
            } else {
                alert(result.message || 'Failed to sync from Microsoft To Do');
            }
        } catch (error) {
            alert(error.response?.data?.message || 'Failed to sync from Microsoft To Do');
        } finally {
            setSyncingTodo(false);
        }
    };

    const handleStatusChange = async (taskId, newStatus) => {
        const result = await updateStatus(taskId, newStatus);
        if (!result.success) {
            alert(result.message);
        }
    };

    const handleDeleteTask = async (taskId) => {
        if (!window.confirm('Are you sure?')) return;
        const result = await deleteTask(taskId);
        if (!result.success) {
            alert(result.message);
        }
    };

    const handleApprovalActionClick = (task, action) => {
        setSelectedTask(task);
        setApprovalAction(action);
        setApprovalRemarks('');
        modals.setShowApprovalModal(true);
    };

    const handleSubmitApproval = async () => {
        const result = await approvalAction_(selectedTask._id, approvalAction, approvalRemarks);
        if (result.success) {
            alert(`Task ${approvalAction}d successfully`);
            modals.setShowApprovalModal(false);
            setApprovalRemarks('');
        } else {
            alert(result.message);
        }
    };

    const handleVerifyActionClick = (task, action) => {
        setSelectedTask(task);
        setVerifyAction(action);
        setVerifyRemarks('');
        modals.setShowVerifyModal(true);
    };

    const handleAddConnectionClick = async () => {
        const taskIdToLink = selectedTaskForLink?._id || linkTaskId?.trim();
        if (!selectedTask || !taskIdToLink) {
            alert('Please search and select a task to link, or enter a task ID');
            return;
        }
        
        const result = await handleAddConnection(selectedTask._id, taskIdToLink, linkType);
        if (result.success) {
            setLinkTaskId('');
            setSelectedTaskForLink(null);
            setLinkSearchQuery('');
            setLinkSearchResults([]);
            setShowLinkDropdown(false);
        } else {
            alert(result.message);
        }
    };

    const handleRemoveConnectionClick = async (connectionId) => {
        if (!selectedTask) return;
        const result = await handleRemoveConnection(selectedTask._id, connectionId);
        if (!result.success) {
            alert(result.message);
        }
    };

    const handleReassignClick = async (assignedTo, reason) => {
        if (!selectedTask) return;
        const result = await reassignTask(selectedTask._id, assignedTo, reason);
        if (result.success) {
            modals.setShowReassignModal(false);
            alert('Task reassigned successfully');
        } else {
            alert(result.message);
        }
    };

    const handleSubmitVerify = async () => {
        const result = await verifyAction_(selectedTask._id, verifyAction, verifyRemarks);
        if (result.success) {
            alert(`Task ${verifyAction === 'verify' ? 'verified' : 'unverified'} successfully`);
            modals.setShowVerifyModal(false);
            setVerifyRemarks('');
        } else {
            alert(result.message);
        }
    };

    const openEnhancedViewModal = (task) => {
        setSelectedTask(task);
        fetchTaskDetails(task._id);
        modals.setShowEnhancedViewModal(true);
    };

    const handleEnhancedModalEdit = () => {
        modals.setShowEditModal(true);
    };

    const handleEnhancedModalReassign = () => {
        modals.setShowReassignModal(true);
    };

    const openCommentsModal = async (task) => {
        setSelectedTask(task);
        await fetchTaskDetails(task._id);
        modals.setShowCommentsModal(true);
        await handleMarkCommentsAsRead(task._id);
    };

    const handleCommentsModalAddComment = async (commentText) => {
        if (!selectedTask || !commentText.trim()) return;
        
        const result = await handleAddComment(selectedTask._id, commentText);
        if (!result.success) {
            throw new Error(result.message);
        }
    };

    const handleSaveEditTask = async (updatedData) => {
        if (!selectedTask) return;
        
        const result = await updateTask(selectedTask._id, updatedData);
        if (result.success) {
            modals.setShowEditModal(false);
            alert('Task updated successfully');
        } else {
            alert(result.message);
        }
    };

    const handleSubtaskStatusChangeWrapper = async (subtaskId, status) => {
        if (!selectedTask) return;
        const result = await updateSubtaskStatus(selectedTask._id, subtaskId, status);
        if (!result.success) {
            alert(result.message);
        }
    };

    const handleAddSubtask = () => {
        modals.setShowCreateSubtaskModal(true);
    };

    const handleEditSubtask = (subtask) => {
        setSelectedTask(subtask);
        modals.setShowEditModal(true);
    };

    const handleDeleteSubtask = async (subtaskId) => {
        if (!selectedTask) return;
        
        try {
            const response = await axiosInstance.delete(`/api/tasks/${subtaskId}`);
            if (response.data.success) {
                alert('Subtask deleted successfully');
                // Refresh subtasks
                await fetchSubtasks(selectedTask._id);
            } else {
                alert(response.data.message || 'Failed to delete subtask');
            }
        } catch (error) {
            console.error('Error deleting subtask:', error);
            alert(error.response?.data?.message || 'Failed to delete subtask');
        }
    };

    const handleCreateSubtaskWrapper = async (subtaskData) => {
        if (!selectedTask) return;
        const result = await createSubtask(selectedTask._id, subtaskData);
        if (result.success) {
            alert('Subtask created successfully');
            modals.setShowCreateSubtaskModal(false);
        } else {
            alert(result.message);
        }
    };

    const resetForm = () => {
        setFormData({ title: '', description: '', assignedTo: [], priority: 'medium', dueDate: '', dueTime: '', tags: '' });
        setAttachmentFiles([]);
    };

    const handleStatCardClick = (filterType, filterValue) => {
        setActiveStatCard(filterType);
        
        if (isManager && activeTab === 'assigned-to-me') {
            if (filterType === 'total') {
                setApprovalStatusFilter('');
            } else if (['pending', 'approved', 'rejected'].includes(filterType)) {
                setApprovalStatusFilter(filterType);
            }
            setCurrentPage(1);
            return;
        }
        
        if (filterType === 'total') {
            setStatusFilter([]);
            setPriorityFilter([]);
            setOverdueFilter(false);
            setApprovalStatusFilter('');
        } else if (filterType === 'overdue') {
            setStatusFilter([]);
            setOverdueFilter(true);
        } else {
            setStatusFilter([filterValue]);
            setOverdueFilter(false);
        }
        
        setCurrentPage(1);
        
        setTimeout(() => {
            const taskListElement = document.querySelector('.tasks-table-container, .grouped-tasks-container');
            if (taskListElement) {
                taskListElement.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                });
            }
        }, 100);
    };

    const renderTaskRow = (task) => {
        return (
            <TaskRow 
                key={task._id}
                task={task}
                user={user}
                isManager={isManager}
                activeTab={activeTab}
                onView={openEnhancedViewModal}
                onComments={openCommentsModal}
                onReassign={(task) => { setSelectedTask(task); modals.setShowReassignModal(true); }}
                onApprove={handleApprovalActionClick}
                onVerify={handleVerifyActionClick}
                onDelete={handleDeleteTask}
                onStatusChange={handleStatusChange}
                formatDate={formatDate}
                formatDateTime={formatDateTime}
                getPriorityColor={getPriorityColor}
                getStatusBadge={getStatusBadge}
                getApprovalStatusBadge={getApprovalStatusBadge}
                isOverdue={isOverdue}
            />
        );
    };

    return (
        <div className="task-management-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="task-management-content">
                <TaskHeader
                    activeTab={activeTab}
                    isManager={isManager}
                    scope={scope}
                />

                {/* Tabs */}
                <TaskTabs
                    activeTab={activeTab}
                    isManager={isManager}
                    scope={scope}
                    onTabChange={(tab) => { setActiveTab(tab); setCurrentPage(1); }}
                />

                {/* Dashboard Stats */}
                <TaskStatistics 
                    stats={stats}
                    managerStats={managerStats}
                    isManager={isManager}
                    activeTab={activeTab}
                    activeStatCard={activeStatCard}
                    onStatCardClick={handleStatCardClick}
                />

                {/* Controls */}
                <TaskFilters 
                    searchTerm={searchTerm}
                    statusFilter={statusFilter}
                    priorityFilter={priorityFilter}
                    approvalStatusFilter={approvalStatusFilter}
                    overdueFilter={overdueFilter}
                    taskTypeFilter={taskTypeFilter}
                    viewMode={viewMode}
                    groupBy={groupBy}
                    isManager={isManager}
                    activeTab={activeTab}
                    canCreate={canCreate}
                    canGroupByDepartment={canSeeBeyondOwn}
                    syncingTodo={syncingTodo}
                    onSyncTodo={handleSyncTodo}
                    onSearchChange={(value) => { setSearchTerm(value); setCurrentPage(1); }}
                    onStatusFilterChange={(value) => { setStatusFilter(value); setCurrentPage(1); }}
                    onPriorityFilterChange={(value) => { setPriorityFilter(value); setCurrentPage(1); }}
                    onApprovalStatusFilterChange={(value) => { setApprovalStatusFilter(value); setCurrentPage(1); }}
                    onOverdueFilterChange={(value) => { setOverdueFilter(value); setCurrentPage(1); }}
                    onTaskTypeFilterChange={(value) => { setTaskTypeFilter(value); setCurrentPage(1); }}
                    onViewModeChange={(newViewMode) => {
                        setViewMode(newViewMode);
                        // Auto-set groupBy for managers in approval tab when switching to grouped view
                        if (newViewMode === 'grouped' && isManager && activeTab === 'assigned-to-me') {
                            setGroupBy('approvalStatus');
                        } else if (newViewMode === 'grouped' && groupBy === 'approvalStatus') {
                            // Reset to status if switching away from approval tab
                            setGroupBy('status');
                        }
                    }}
                    onGroupByChange={(value) => setGroupBy(value)}
                    onCreateTask={() => modals.setShowCreateModal(true)}
                />

                {/* Task List or Grouped View */}
                {loading ? (
                    <div className="loading-state">Loading tasks...</div>
                ) : viewMode === 'list' ? (
                    // List View
                    tasks.length === 0 ? (
                        <div className="empty-state">No tasks found</div>
                    ) : (
                        <>
                            <div className="tasks-table-container">
                                <TaskTable 
                                    tasks={tasks}
                                    activeTab={activeTab}
                                    isManager={isManager}
                                    renderRow={renderTaskRow}
                                />
                            </div>
                            <Pagination 
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onChange={setCurrentPage}
                            />
                        </>
                    )
                ) : (
                    // Grouped View
                    groupedTasks.length === 0 ? (
                        <div className="empty-state">No tasks found</div>
                    ) : (
                        <div className="grouped-tasks-container">
                            {groupedTasks.map((group, groupIndex) => (
                                <div key={groupIndex} className="task-group">
                                    <div className="group-header" onClick={() => toggleGroup(groupIndex)}>
                                        <span className="group-toggle">{expandedGroups[groupIndex] ? '▼' : '▶'}</span>
                                        <h3 className="group-title">
                                            {groupBy === 'user' && (
                                                <span className="group-label">Assigned To: </span>
                                            )}
                                            {group.groupName}
                                        </h3>
                                        <span className="group-count">({group.count} tasks)</span>
                                    </div>
                                    {expandedGroups[groupIndex] && (
                                        <div className="group-content">
                                            {group.tasks.length === 0 ? (
                                                <div className="empty-group">No tasks in this group</div>
                                            ) : (
                                                <TaskTable 
                                                    tasks={group.tasks}
                                                    activeTab={activeTab}
                                                    isManager={isManager}
                                                    renderRow={renderTaskRow}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )
                )}
            </div>

            {/* Create Task Modal */}
            <CreateTaskModal 
                show={modals.showCreateModal}
                formData={formData}
                attachmentFiles={attachmentFiles}
                onClose={() => modals.setShowCreateModal(false)}
                onSubmit={handleCreateTaskSubmit}
                onFormDataChange={setFormData}
                onAttachmentFilesChange={setAttachmentFiles}
                onShowUserSlideOver={() => modals.setShowUserSlideOver(true)}
            />

            {/* Approval Modal */}
            <ApprovalModal 
                show={modals.showApprovalModal}
                task={selectedTask}
                action={approvalAction}
                remarks={approvalRemarks}
                onRemarksChange={setApprovalRemarks}
                onSubmit={handleSubmitApproval}
                onClose={() => modals.setShowApprovalModal(false)}
            />

            {/* User Selection Slide-Over Panel */}
            <UserSelectSlideOver 
                show={modals.showUserSlideOver}
                users={users}
                selectedUsers={formData.assignedTo}
                onChange={(selectedUsers) => setFormData({ ...formData, assignedTo: selectedUsers })}
                onClose={() => modals.setShowUserSlideOver(false)}
            />

            {/* Verification Modal */}
            {modals.showReassignModal && selectedTask && (
                <ReassignTaskModal
                    task={selectedTask}
                    users={users}
                    onClose={() => modals.setShowReassignModal(false)}
                    onReassign={handleReassignClick}
                />
            )}

            {/* Enhanced View Task Modal */}
            {modals.showEnhancedViewModal && selectedTask && (
                <ViewTaskModal
                    task={selectedTask}
                    subtasks={subtasks}
                    subtasksLoading={subtasksLoading}
                    onClose={() => modals.setShowEnhancedViewModal(false)}
                    onEdit={handleEnhancedModalEdit}
                    onReassign={handleEnhancedModalReassign}
                    onSubtaskStatusChange={handleSubtaskStatusChangeWrapper}
                    onAddSubtask={handleAddSubtask}
                    onEditSubtask={handleEditSubtask}
                    onDeleteSubtask={handleDeleteSubtask}
                    formatDate={formatDate}
                    formatDateTime={formatDateTime}
                />
            )}

            {/* Enhanced Edit Task Modal */}
            {modals.showEditModal && selectedTask && (
                <EditTaskModal
                    task={selectedTask}
                    subtasks={subtasks}
                    subtasksLoading={subtasksLoading}
                    onClose={() => modals.setShowEditModal(false)}
                    onSave={handleSaveEditTask}
                    onSubtaskStatusChange={handleSubtaskStatusChangeWrapper}
                    onAddSubtask={handleAddSubtask}
                    fetchSubtasks={fetchSubtasks}
                />
            )}

            {/* Create Subtask Modal */}
            {modals.showCreateSubtaskModal && selectedTask && (
                <CreateSubtaskModal
                    parentTask={selectedTask}
                    users={Object.values(users).flat()}
                    onClose={() => modals.setShowCreateSubtaskModal(false)}
                    onCreate={handleCreateSubtaskWrapper}
                />
            )}

            {/* Comments Modal */}
            {modals.showCommentsModal && selectedTask && (
                <CommentsModal
                    task={selectedTask}
                    onClose={() => modals.setShowCommentsModal(false)}
                    onAddComment={handleCommentsModalAddComment}
                    formatDate={formatDate}
                />
            )}

            {/* Verify Task Modal */}
            <VerifyTaskModal 
                show={modals.showVerifyModal}
                task={selectedTask}
                action={verifyAction}
                remarks={verifyRemarks}
                onRemarksChange={setVerifyRemarks}
                onSubmit={handleSubmitVerify}
                onClose={() => modals.setShowVerifyModal(false)}
            />
        </div>
    );
};

export default TaskManagement;
